import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import {
  Box,
  Button,
  useMediaQuery,
  useTheme,
  Link,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import NextLink from 'next/link';
import { useRouter } from 'next/navigation';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import {
  getAuth0Variant,
  getCurrentStep,
  getExperiments,
  getInputs,
  getIsMobileApp,
  getIsSignedIn,
  getUserEmail,
} from 'src/redux/selectors';
import {
  isAuth0PopupExperimentActive,
  isNerdyTipAndProgressBarActive,
  isProgressBarOnlyActive,
} from 'src/lib/experiments';
import { Screen } from 'src/lib/flows/screens';
import { NerdyTip } from './shared/nerdy-tip';
import { datadogRum } from '@datadog/browser-rum';
import { getActiveFlow } from 'src/redux/actions/flow';
import { getProgressValues } from 'src/lib/quiz-utils';
import { IntroAPROrRewards, PrimaryGoal } from 'src/types/inputs';
import { getNextQuestion } from 'src/redux/actions/form';
import { Input, Routes } from 'src/lib/constants';
import { signIn } from 'src/redux/reducers/user-data';
import {
  SEGMENT_DEPLOYABLE_NAME,
  SEGMENT_DEPLOYABLE_VERSION,
} from 'src/utils/analytics/constants';
import {
  useAnalytics,
  useAnalyticsPageView,
} from '@nerdwallet-cards/analytics';
import { useAnonymousId } from 'src/utils/analytics/hooks';
import { Auth0Analytics } from '../screens/auth0';
import { useSaveUserData } from 'src/utils/user-data/hooks';
import { buildResultsUrl } from 'src/redux/actions/terminate-quiz-flow';
import { v4 as uuid } from 'uuid';

const enableScrollLocks = (element: HTMLElement): void => {
  disableBodyScroll(element);
};

const disableScrollLocks = (): void => {
  // clear body scroll lock set in CDM
  clearAllBodyScrollLocks();
};

const isScrollable = (element: HTMLElement): boolean => {
  if (!element) {
    return false;
  }

  // check if scrollHeight is greater than clientHeight
  // this means there is room to scroll and we are not at the bottom of the container
  return element.scrollHeight > element.clientHeight;
};

type PrimaryCta = {
  isDisabled?: boolean;
  onClick?: () => void;
  text: string;
  style?: Record<string, any> | null;
  showSignInContent?: boolean;
  skipLink?: {
    href: string;
    label: string;
    onClick: () => void;
    sxProps?: SxProps<Theme>;
  } | null;
  terms?: React.ReactNode;
};

type ScreenContainerProps = {
  /** properties of the submit button, or primary cta */
  primaryCta?: PrimaryCta;
  /** children that will be placed between nav buttons and submit button */
  children?: React.ReactNode;
  /** Additional content added below the CTA */
  additionalContent?: React.ReactNode;
  /** Additional content added on top of the CTA */
  additionalContentTop?: React.ReactNode;
  /** Helper text to display below the CTA */
  helperText?: string | JSX.Element;
  /** Nerdy Tip text to display near the CTA */
  nerdyTip?: string;
};
const ScreenContainer: React.FC<ScreenContainerProps> = ({
  primaryCta = {
    isDisabled: false,
    onClick() {},
    text: '',
    style: null,
    skipLink: null,
    terms: null,
  },
  children,
  additionalContent,
  additionalContentTop,
  helperText,
  nerdyTip = '',
}) => {
  const { text: buttonText, isDisabled, terms } = primaryCta;
  const theme = useTheme();
  const router = useRouter();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isContainerBottom, setIsContainerBottom] = useState(true);
  const scrollableContainer = useRef<HTMLDivElement>(null);
  const experiments = useAppSelector(getExperiments);
  const screenName = useAppSelector(getCurrentStep);
  const inputs = useAppSelector(getInputs);
  const dispatch = useAppDispatch();
  const userEmail = useAppSelector(getUserEmail);
  const currentFlow = dispatch(getActiveFlow());
  const isSignedIn = useAppSelector(getIsSignedIn);
  const { pageViewId, pageName } = useAnalyticsPageView();
  const isMobileApp = useAppSelector(getIsMobileApp);
  const auth0Variant = useAppSelector(getAuth0Variant);
  const saveUserData = useSaveUserData();
  const anonymousId = useAnonymousId();
  const showAuth0Page =
    !isAuth0PopupExperimentActive(auth0Variant) && !isSignedIn && !isMobileApp;
  const forceAuth0Page =
    isAuth0PopupExperimentActive(auth0Variant) &&
    isMobile &&
    !isSignedIn &&
    !isMobileApp;
  const introAprOrRewardsEnd =
    screenName === Screen.IntroAPROrRewards &&
    inputs[Input.IntroAPROrRewards] === IntroAPROrRewards.IntroAPR;
  const isEndScreen =
    screenName === Screen.MonthlySpend ||
    screenName === Screen.AnnualFees ||
    screenName === Screen.PurchaseSpendInput ||
    introAprOrRewardsEnd;
  const [auth0Redirect, setAuth0Redirect] = useState(false);

  const screen = currentFlow[screenName];
  const nextScreen = useMemo(
    () => dispatch(getNextQuestion(screen)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, screen, inputs, userEmail]
  );
  const formSubmitId = uuid();
  const { deviceId, deployableName, deployableVersion } = useAnalytics();

  const getMarginTopForNerdyTip = (defaultValue: number): number => {
    return isNerdyTipAndProgressBarActive(experiments) ? 4 : defaultValue;
  };

  useEffect(() => {
    const redirectToAuth0 = async () => {
      if (!anonymousId || !auth0Redirect) return;
      const auth0Analytics: Auth0Analytics = {
        anonymousUserId: anonymousId,
        mostRecentTraditionalDeviceId: deviceId,
        cancelCta: 'No, let me see my results',
        deployableName: deployableName,
        deployableVersion: deployableVersion,
        driverLocation: 'cardfinder_flow',
        driverPageName: pageName,
        driverPageViewId: pageViewId,
        driverPageUrl: `${window.location.protocol}//${window.location.host}${Routes.Flow}/${screenName}`,
      };
      const saveInputResponse = await saveUserData(inputs);
      const redirectUrl = dispatch(
        buildResultsUrl({
          formSubmitId,
          saveInputResponse,
          auth0Analytics,
          redirectType: 'signup',
        })
      );
      window.location.assign(redirectUrl);
      setAuth0Redirect(false);
    };
    redirectToAuth0();
  }, [anonymousId, auth0Redirect]);

  useEffect(() => {
    router.prefetch(`${Routes.Flow}/${nextScreen}`);
  }, [nextScreen, router]);

  const onPrimaryCTASubmit = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();

      if (isDisabled) {
        return;
      }

      datadogRum.addAction('Next', {
        origin: screenName,
      });

      if (isEndScreen && (showAuth0Page || forceAuth0Page)) {
        setAuth0Redirect(true);
      } else {
        if (primaryCta?.onClick) {
          primaryCta.onClick();
        }
        // This could be an anchor click, or a form submit, so this OR condition is important if we want "enter key to submit" to work
        router.push(`${Routes.Flow}/${nextScreen}`);
      }
    },
    [
      isDisabled,
      screenName,
      isEndScreen,
      showAuth0Page,
      forceAuth0Page,
      primaryCta,
      router,
      nextScreen,
    ]
  );

  const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (!e.target || e.target !== scrollableContainer.current) {
      return;
    }
    const target = e.target as HTMLDivElement;

    // if scroll and client height are equal, there is not more room to scroll
    // so we are at the bottom of the container
    const isBottom =
      Math.round(target.scrollHeight - Math.ceil(target.scrollTop)) <=
      target.clientHeight;
    setIsContainerBottom(isBottom);
  }, []);

  useEffect(() => {
    if (scrollableContainer.current) {
      if (isMobile) {
        enableScrollLocks(scrollableContainer.current);
      } else {
        disableScrollLocks();
      }
      setIsContainerBottom(!isScrollable(scrollableContainer.current));
    }
    return () => disableScrollLocks();
  }, [scrollableContainer, isMobile]);

  const isRegScreen = screenName === Screen.Register;

  const progressValues = getProgressValues(
    screen?.progress !== undefined && typeof screen?.progress === 'function'
      ? screen?.progress(inputs)
      : screen?.progress,
    screenName,
    inputs.primaryGoal as PrimaryGoal
  );

  const getMarginTop = useCallback(() => {
    if (progressValues.show && screen?.progressRate) return 6.9;

    if (
      isNerdyTipAndProgressBarActive(experiments) ||
      isProgressBarOnlyActive(experiments)
    )
      return 6.9;

    return 4.5;
  }, [progressValues.show, screen, experiments]);

  const onSignInClick = () => {
    window.nwapi.ui.showAuthDialog({
      authType: 'signin',
      flow: 'onboarding-generic',
      version: 2,
      segmentTrackingAttributes: {
        driverPageName: pageName,
        deployableName: SEGMENT_DEPLOYABLE_NAME,
        deployableVersion: SEGMENT_DEPLOYABLE_VERSION,
        driverPageViewId: pageViewId,
        driverFlowName: SEGMENT_DEPLOYABLE_NAME,
      },
      onSuccess() {
        router.push(nextScreen);
      },
      onStateChange(state: any) {
        if (state?.identity?.isSignedIn && !isSignedIn) {
          dispatch(signIn(true));
        }
      },
    });
  };

  return (
    <Box
      component="form"
      onSubmit={onPrimaryCTASubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
      }}
    >
      <Box
        sx={{
          [theme.breakpoints.down('sm')]: {
            padding: 3,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflowY: 'scroll',
          },
        }}
        ref={scrollableContainer}
        onScroll={onScroll}
      >
        <Box
          sx={{
            flexGrow: 1,
            marginTop: getMarginTopForNerdyTip(0),
            [theme.breakpoints.down('md')]: {
              marginTop: getMarginTop(),
            },
          }}
        >
          {children}
          {additionalContent && (
            <Box
              display={{
                xs: 'block',
                sm: 'none',
              }}
            >
              {additionalContent}
            </Box>
          )}
        </Box>
      </Box>
      {primaryCta.text && (
        <Box display="flex" flexDirection="column">
          <Box
            sx={
              primaryCta.style ?? {
                padding: 3,
                [theme.breakpoints.up('sm')]: {
                  padding: 0,
                  marginTop: isRegScreen ? 3 : getMarginTopForNerdyTip(7),
                  width: '26%',
                },
                [theme.breakpoints.down('sm')]: {
                  zIndex: 1,
                  padding: 3,
                  width: '100%',
                  ...(!isContainerBottom && {
                    boxShadow: `0 -4px 8px 0 ${theme.palette.neutral.lighter}, 0 -8px 16px 0 ${theme.palette.neutral.lighter}`,
                  }),
                },
              }
            }
          >
            <Box
              display={{
                xs: 'block',
                sm: 'none',
              }}
            >
              {additionalContentTop && (
                <Box
                  display={{
                    xs: 'block',
                    sm: 'none',
                  }}
                >
                  {additionalContentTop}
                </Box>
              )}
              <NerdyTip text={nerdyTip} />
            </Box>
            {terms && terms}
            <Button type="submit" color="primary" disabled={isDisabled}>
              {buttonText}
            </Button>
            {helperText && typeof helperText === 'string' ? (
              <Typography variant="bodySmall" color="secondary.main">
                {helperText}
              </Typography>
            ) : (
              <>{helperText}</>
            )}
            {primaryCta?.showSignInContent && (
              <Box display="flex" justifyContent="center" marginTop={3}>
                <Typography variant="textSmall" marginRight={1}>
                  Already have an account?
                </Typography>
                <Link
                  variant="textSmall"
                  component="button"
                  type="button"
                  onClick={onSignInClick}
                >
                  Sign In
                </Link>
              </Box>
            )}
            {primaryCta?.skipLink?.label && primaryCta?.skipLink?.href && (
              <Link
                component={NextLink}
                href={primaryCta.skipLink.href}
                onClick={primaryCta?.skipLink?.onClick}
                aria-label="See my results"
                variant="text"
                sx={{ ...(primaryCta?.skipLink?.sxProps || {}) }}
              >
                {primaryCta.skipLink.label}
              </Link>
            )}
            <Box display={{ xs: 'none', sm: 'block' }}>
              <NerdyTip text={nerdyTip} />
            </Box>
          </Box>
        </Box>
      )}
      {additionalContent && (
        <Box
          display={{
            xs: 'none',
            sm: 'block',
          }}
        >
          {additionalContent}
        </Box>
      )}
    </Box>
  );
};

export default ScreenContainer;
